import {
  LicenseCategoryLabels,
  LicenseCategoryPriority,
  getSubCategory,
  COCSubCategoryLabels,
  SmallVesselSubCategoryLabels,
  RadioOperatorSubCategoryLabels,
  DangerousGoodsHandlingOfficerSubCategoryLabels,
} from "@/views/enterprise/management/crews/crew-settings/constants/License.js";
import { toYMDHM } from "../../../filter";

export default {
  /**
   * ダッシュボードに表示する資格定義リストを取得
   * AccordionDataコンポーネントに合う形式で返す
   */
  getLicenseDefinitionForDashboard: state => {
    const dashboardLicenses = state.license_definition;

    // サブカテゴリタイプからラベルを取得する関数
    const getSubCategoryLabel = (category, subCategoryType) => {
      if (!subCategoryType) return null;

      // カテゴリごとに適切なラベルマップを使用
      if (category === "COC") {
        return COCSubCategoryLabels[subCategoryType] || null;
      }
      if (category === "SMALL_BOAT_OPERATOR_LICENSE") {
        return SmallVesselSubCategoryLabels[subCategoryType] || null;
      }
      if (category === "RADIO_OPERATOR") {
        return RadioOperatorSubCategoryLabels[subCategoryType] || null;
      }
      if (category === "DANGEROUS_GOODS_HANDLING_OFFICER") {
        return (
          DangerousGoodsHandlingOfficerSubCategoryLabels[subCategoryType] ||
          null
        );
      }

      return null;
    };

    // まず、カテゴリごとにサブカテゴリの数を集計
    const subCategoryCountMap = new Map();
    // カテゴリ・サブカテゴリごとに小アイテム（license）の数を集計
    const licenseCountBySubCategoryMap = new Map();

    dashboardLicenses.forEach(license => {
      const category = license.category;
      const subcategoryNo = license.subcategory_no;

      if (category && subcategoryNo) {
        // サブカテゴリの数を集計
        if (!subCategoryCountMap.has(category)) {
          subCategoryCountMap.set(category, new Set());
        }
        subCategoryCountMap.get(category).add(subcategoryNo);

        // サブカテゴリごとの小アイテム数を集計
        const key = `${category}_${subcategoryNo}`;
        if (!licenseCountBySubCategoryMap.has(key)) {
          licenseCountBySubCategoryMap.set(key, 0);
        }
        licenseCountBySubCategoryMap.set(
          key,
          licenseCountBySubCategoryMap.get(key) + 1
        );
      }
    });

    // カテゴリごとにグループ化
    const categoryMap = new Map();

    dashboardLicenses.forEach(license => {
      const category = license.category;
      const subcategoryNo = license.subcategory_no;

      // カテゴリが存在しない場合は作成
      if (!categoryMap.has(category)) {
        categoryMap.set(category, {
          title: LicenseCategoryLabels[category] || category,
          accordionData: [],
        });
      }

      const categoryData = categoryMap.get(category);

      // サブカテゴリがある場合
      if (subcategoryNo) {
        const subCategorySet = subCategoryCountMap.get(category);
        const subCategoryCount = subCategorySet
          ? subCategoryCountMap.get(category).size
          : 0;
        const key = `${category}_${subcategoryNo}`;
        const licenseCountInSubCategory =
          licenseCountBySubCategoryMap.get(key) || 0;

        // その他（OTHERS）カテゴリの場合は常にサブカテゴリを省略してフラットに追加
        // または、サブカテゴリが1つしかない場合、またはサブカテゴリ内の小アイテムが1つしかない場合は、サブカテゴリを省略してフラットに追加
        if (
          category === "OTHERS" ||
          subCategoryCount === 1 ||
          licenseCountInSubCategory === 1
        ) {
          const subCategoryType = getSubCategory(category, subcategoryNo);
          categoryData.accordionData.push({
            label: license.name,
            id: license.id,
            category: category,
            subCategory: subCategoryType,
          });
        } else {
          // サブカテゴリが複数あり、かつサブカテゴリ内の小アイテムが複数ある場合は、サブカテゴリの親アイテムを作成
          const subCategoryType = getSubCategory(category, subcategoryNo);
          const subCategoryLabel = subCategoryType
            ? getSubCategoryLabel(category, subCategoryType) ||
              `サブカテゴリ${subcategoryNo}`
            : `サブカテゴリ${subcategoryNo}`;

          // サブカテゴリの親アイテムを探す
          let subCategoryItem = categoryData.accordionData.find(
            item => item.label === subCategoryLabel
          );

          // サブカテゴリの親アイテムが存在しない場合は作成
          if (!subCategoryItem) {
            subCategoryItem = {
              label: subCategoryLabel,
              children: [],
            };
            categoryData.accordionData.push(subCategoryItem);
          }

          // 子アイテムを追加（onClickはコンポーネント側で追加）
          subCategoryItem.children.push({
            label: license.name,
            id: license.id,
            category: category,
            subCategory: subCategoryType,
          });
        }
      } else {
        // サブカテゴリがない場合は直接追加（onClickはコンポーネント側で追加）
        categoryData.accordionData.push({
          label: license.name,
          id: license.id,
          category: category,
          subCategory: null,
        });
      }
    });

    // Mapを配列に変換
    return Array.from(categoryMap.values());
  },

  /**
   * ダッシュボードに表示する保有資格リストを取得
   * enterprise_crew_license_idが同一のものは同一の資格とみなし、start_dateが最新のものだけを扱う
   */
  getHavingLicenseForDashboard: state => {
    // enterprise_crew_license_idごとにグループ化
    const licenseMap = new Map();

    state.having_license.forEach(license => {
      const licenseId = license.enterprise_crew_license_id;
      if (!licenseId) return;

      // 既存のエントリがない場合、またはstart_dateがより新しい場合に更新
      if (!licenseMap.has(licenseId)) {
        licenseMap.set(licenseId, license);
      } else {
        const existingLicense = licenseMap.get(licenseId);
        const existingStartDate = existingLicense.start_date
          ? new Date(existingLicense.start_date)
          : null;
        const currentStartDate = license.start_date
          ? new Date(license.start_date)
          : null;

        // start_dateがより新しいものを選択
        if (
          currentStartDate &&
          (!existingStartDate || currentStartDate > existingStartDate)
        ) {
          licenseMap.set(licenseId, license);
        }
      }
    });

    // 最新の保有資格のみを取得し、定義情報と結合してstatusを計算
    return Array.from(licenseMap.values())
      .map(license => {
        const licenseDefinition = state.license_definition.find(
          definition => definition.id === license.enterprise_crew_license_id
        );

        if (!licenseDefinition) {
          return null;
        }

        // statusを計算
        let status = "";
        if (license.expiration_date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const expirationDate = new Date(license.expiration_date);
          expirationDate.setHours(0, 0, 0, 0);

          // 期限切れかどうか
          if (expirationDate < today) {
            status = "caution"; // 期限切れ
          } else {
            // 残り日数を計算
            const daysUntilExpiration = Math.ceil(
              (expirationDate - today) / (1000 * 60 * 60 * 24)
            );

            const alertDays =
              licenseDefinition.expiration_alert_days !== undefined
                ? licenseDefinition.expiration_alert_days
                : 60;
            const warningDays =
              licenseDefinition.expiration_warning_days !== undefined
                ? licenseDefinition.expiration_warning_days
                : 180;

            // アラート日数以内
            if (daysUntilExpiration <= alertDays) {
              status = "caution";
            }
            // 警告日数以内（アラート日数より長い）
            else if (daysUntilExpiration <= warningDays) {
              status = "warning";
            }
            // それ以外は空文字列
          }
        }

        return {
          ...license,
          category: LicenseCategoryLabels[licenseDefinition.category],
          categoryCode: licenseDefinition.category, // カテゴリコードを追加（遷移時に使用）
          name: licenseDefinition.name,
          expiration: licenseDefinition.license_term_years
            ? `${licenseDefinition.license_term_years}年`
            : "-",
          nextUpdateDate: license.expiration_date
            ? license.expiration_date.replace(/-/g, "/")
            : "-",
          status: status,
        };
      })
      .filter(license => license !== null) // nullを除外
      .sort((a, b) => {
        // ソート優先順位: warning > caution > その他
        const statusPriority = {
          warning: 1,
          caution: 2,
          "": 3,
        };
        const priorityA = statusPriority[a.status] || 3;
        const priorityB = statusPriority[b.status] || 3;
        return priorityA - priorityB;
      });
  },

  /**
   * 保有している全ての資格のcategory情報（labelとcategory）を返す
   */
  getHavingLicenseCategories: state => {
    // enterprise_crew_license_idごとにグループ化（最新のstart_dateのみ）
    const licenseMap = new Map();

    state.having_license.forEach(license => {
      const licenseId = license.enterprise_crew_license_id;
      if (!licenseId) return;

      // 既存のエントリがない場合、またはstart_dateがより新しい場合に更新
      if (!licenseMap.has(licenseId)) {
        licenseMap.set(licenseId, license);
      } else {
        const existingLicense = licenseMap.get(licenseId);
        const existingStartDate = existingLicense.start_date
          ? new Date(existingLicense.start_date)
          : null;
        const currentStartDate = license.start_date
          ? new Date(license.start_date)
          : null;

        // start_dateがより新しいものを選択
        if (
          currentStartDate &&
          (!existingStartDate || currentStartDate > existingStartDate)
        ) {
          licenseMap.set(licenseId, license);
        }
      }
    });

    // カテゴリ情報を取得（重複を排除）
    const categorySet = new Set();
    Array.from(licenseMap.values()).forEach(license => {
      const licenseDefinition = state.license_definition.find(
        definition => definition.id === license.enterprise_crew_license_id
      );
      if (licenseDefinition && licenseDefinition.category) {
        categorySet.add(licenseDefinition.category);
      }
    });

    // 定義済みの優先度に従って並び替え
    const sortedCategories = Array.from(categorySet).sort((a, b) => {
      const priorityA =
        LicenseCategoryPriority[a] !== undefined
          ? LicenseCategoryPriority[a]
          : Number.MAX_SAFE_INTEGER;
      const priorityB =
        LicenseCategoryPriority[b] !== undefined
          ? LicenseCategoryPriority[b]
          : Number.MAX_SAFE_INTEGER;
      return priorityA - priorityB;
    });

    // カテゴリ情報を配列に変換
    return sortedCategories.map(category => ({
      category: category,
      label: LicenseCategoryLabels[category] || category,
    }));
  },

  /**
   * カテゴリごとに保有資格リストを取得（有効性判定付き）
   * @param {String} category - カテゴリタイプ
   * @returns {Object} { categoryLabel: string, licenses: Array }
   */
  getHavingLicenseListByCategory: state => category => {
    if (!category) {
      return {
        categoryLabel: "",
        licenses: [],
      };
    }

    /** カテゴリに該当する保有資格と資格定義を抽出 */
    const categoryLicenses = state.having_license
      .map(license => {
        // 保有している資格の資格定義を取得
        const licenseDefinition = state.license_definition.find(
          definition => definition.id === license.enterprise_crew_license_id
        );

        // カテゴリが一致しない場合は除外
        if (!licenseDefinition || licenseDefinition.category !== category) {
          return null;
        }

        return {
          license,
          licenseDefinition,
        };
      })
      .filter(item => item !== null);

    /** 有効性判定用の今日の日付 */
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parseDate = value => {
      if (!value) {
        return null;
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const licensesWithStatus = categoryLicenses.map(
      ({ license, licenseDefinition }) => {
        /** 保有資格の次回更新期限 */
        const currentExpirationDate = parseDate(license.expiration_date);
        /** 保有資格の取得日 */
        const currentStartDate = parseDate(license.start_date);

        /** 同じ資格ID（enterprise_crew_license_id）の履歴を収集 */
        const sameLicenseEntries = categoryLicenses.filter(
          item =>
            item.license.id !== license.id &&
            item.license.enterprise_crew_license_id ===
              license.enterprise_crew_license_id
        );

        /**
         * 同一資格IDの中に、現在の資格より「新しい / 有効な」レコードが存在するかを判定する
         */
        const hasReplacementFromSameLicense = sameLicenseEntries.some(
          ({ license: otherLicense }) => {
            /** 同じ資格IDの履歴の次回更新期限 */
            const otherExpirationDate = parseDate(otherLicense.expiration_date);
            /** 同じ資格IDの履歴の取得日 */
            const otherStartDate = parseDate(otherLicense.start_date);

            // 履歴のものに有効期限があり、有効期限内の場合
            if (otherExpirationDate && otherExpirationDate >= today) {
              if (!currentExpirationDate) {
                return true;
              }
              return otherExpirationDate > currentExpirationDate;
            }

            // 有効期限が空の場合、または期限切れの場合
            // 取得日が現在のものより履歴のものが新しい場合
            if (
              currentStartDate &&
              otherStartDate &&
              otherStartDate > currentStartDate
            ) {
              return true;
            }

            // 現在のものに開始日がなく、履歴のものに開始日がある場合
            if (!currentStartDate && otherStartDate) {
              return true;
            }

            return false;
          }
        );

        /**
         * 同じ小区分（subcategory）に属する別資格の中で、ランクが同等以上 かつ 有効期限内のものが存在するかを判定する。
         */
        const sameSubCategoryLicenses = categoryLicenses.filter(
          item =>
            item.license.id !== license.id &&
            item.license.enterprise_crew_license_id !==
              license.enterprise_crew_license_id &&
            item.licenseDefinition &&
            item.licenseDefinition.subcategory_no ===
              licenseDefinition.subcategory_no
        );

        const hasHigherOrEqualRank = sameSubCategoryLicenses.some(
          ({ license: otherLicense, licenseDefinition: otherDefinition }) => {
            if (!otherDefinition) {
              return false;
            }

            const otherRank = otherDefinition.rank_in_subcategory;
            const currentRank = licenseDefinition.rank_in_subcategory;

            if (
              otherRank === undefined ||
              otherRank === null ||
              currentRank === undefined ||
              currentRank === null
            ) {
              return false;
            }

            const otherExpirationDate = parseDate(otherLicense.expiration_date);
            if (!otherExpirationDate || otherExpirationDate < today) {
              return false;
            }

            return otherRank <= currentRank;
          }
        );

        let isValid = false;
        let statusReason = "";
        let lastUpdatedDisplay = "";

        // 有効期限が空なら基本的に「有効」、ただし同じ資格で新しいものがあれば「更新済」
        if (!currentExpirationDate) {
          // expiration_dateがない場合
          isValid = true;
          statusReason = hasReplacementFromSameLicense ? "更新済" : "有効";
        } else if (currentExpirationDate >= today) {
          // 有効期限内
          isValid = true;
          statusReason = "有効";
        } else {
          // 期限切れ
          if (hasReplacementFromSameLicense || hasHigherOrEqualRank) {
            isValid = true;
            statusReason = "更新済";
          } else {
            isValid = false;
            statusReason = "無効";
          }
        }

        if (license.updated_at) {
          const updatedDate = new Date(license.updated_at);
          if (!isNaN(updatedDate.getTime())) {
            lastUpdatedDisplay = toYMDHM(updatedDate);
          }
        }

        return {
          ...license,
          name: licenseDefinition.name,
          licenseDefinition: licenseDefinition,
          isValid: isValid,
          statusReason: statusReason,
          lastUpdated: lastUpdatedDisplay,
          crew_having_license_files: license.crew_having_license_files || [],
        };
      }
    );

    const getCategoryPriority = definition => {
      if (!definition || !definition.category) {
        return Number.MAX_SAFE_INTEGER;
      }
      const priority = LicenseCategoryPriority[definition.category];
      return priority !== undefined ? priority : Number.MAX_SAFE_INTEGER;
    };

    const getSubCategoryPriority = definition => {
      if (
        !definition ||
        definition.subcategory_no === undefined ||
        definition.subcategory_no === null
      ) {
        return Number.MAX_SAFE_INTEGER;
      }
      return Number(definition.subcategory_no);
    };

    const getStartDateValue = license => {
      if (!license.start_date) {
        return 0;
      }
      const timestamp = new Date(license.start_date).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    // 表示順序: 大区分 > 小区分番号 > 取得日（新しい順）
    const sortedLicenses = licensesWithStatus.slice().sort((a, b) => {
      const categoryDiff =
        getCategoryPriority(a.licenseDefinition) -
        getCategoryPriority(b.licenseDefinition);
      if (categoryDiff !== 0) {
        return categoryDiff;
      }

      const subCategoryDiff =
        getSubCategoryPriority(a.licenseDefinition) -
        getSubCategoryPriority(b.licenseDefinition);
      if (subCategoryDiff !== 0) {
        return subCategoryDiff;
      }

      const dateDiff = getStartDateValue(b) - getStartDateValue(a);
      if (dateDiff !== 0) {
        return dateDiff;
      }

      return 0;
    });

    return {
      categoryLabel: LicenseCategoryLabels[category] || category,
      licenses: sortedLicenses,
    };
  },

  /**
   * カテゴリごとの選択肢一覧を取得（フォーム用）
   * @param {String} category - カテゴリタイプ（省略可）
   * @returns {Array<{id: string|number, label: string}>}
   */
  getLicenseOptionsByCategory: state => category => {
    const definitions = state.license_definition || [];
    const filtered = category
      ? definitions.filter(def => def.category === category)
      : definitions;
    return filtered.map(def => ({
      id: def.id,
      label: def.name,
    }));
  },

  /**
   * 資格IDから有効期限（年数）を取得
   * @param {String|Number} licenseId - 資格ID
   * @returns {Number|null} 有効期限（年数）、存在しない場合はnull
   */
  getLicenseTermYearsById: state => licenseId => {
    if (!licenseId) {
      return null;
    }
    const definition = state.license_definition.find(
      def => def.id === licenseId
    );
    return definition &&
      definition.license_term_years !== undefined &&
      definition.license_term_years !== null
      ? definition.license_term_years
      : null;
  },
};
